import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";

function campusVariants(campus) {
  const c = String(campus || "").trim();
  const lc = c.toLowerCase();
  if (!lc) return [];
  if (lc === "gapan") return ["gapan", "Gapan", "gapan city", "Gapan City"];
  if (lc === "bulacan") return ["bulacan", "Bulacan"];
  return Array.from(new Set([lc, c]));
}
function norm(s) {
  return String(s || "").trim().toLowerCase();
}
function campusMatch(value, expectedCampus) {
  return campusVariants(expectedCampus).map(norm).includes(norm(value));
}
function sectionMatch(student, section) {
  const target = norm(section);
  const vals = [];
  const secArr = student?.sections;
  if (Array.isArray(secArr)) vals.push(...secArr.map(x => String(x || "")));
  else if (typeof secArr === "string") vals.push(...secArr.split(","));
  vals.push(String(student?.section || ""));
  return vals.some(v => norm(v) === target);
}

function splitSections(obj) {
  if (Array.isArray(obj)) return obj.map(s => String(s).trim()).filter(Boolean);
  if (obj && Array.isArray(obj.sections)) return obj.sections.map(s => String(s).trim()).filter(Boolean);
  return String(obj || "").split(",").map(s => s.trim()).filter(Boolean);
}

function formatName(s) {
  const last = String(s.lastName || "").trim();
  const first = String(s.firstName || "").trim();
  const middle = String(s.middleName || s.middleInitial || "").trim();
  const core = `${last}${last && first ? ", " : ""}${first}`;
  return middle ? `${core} ${middle}` : core;
}

export default function ClassRoster() {
  const user = getCurrentUser();
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const debug = params.get("debug") === "1";
  const campus = String(params.get("campus") || user?.campus || "gapan").toLowerCase();
  const grade = String(params.get("grade") || "");
  const section = String(params.get("section") || "");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    (async () => {
      if (!campus || !grade || !section) return;
      setLoading(true);
      try {
        const base = collection(db, "students");
        const all = new Map();
        const snap = await getDocs(base);
        snap.docs.forEach(d => all.set(d.id, { id: d.id, ...d.data() }));
        const allRows = Array.from(all.values());
        const afterCampus = allRows.filter(s => campusMatch(s.campus, campus));
        const afterGrade = afterCampus.filter(s => norm(s.gradeLevel) === norm(grade));
        const afterSection = afterGrade.filter(s => sectionMatch(s, section));
        const rows = afterSection;
        rows.sort((a, b) => formatName(a).localeCompare(formatName(b)));
        setStudents(rows);
        if (debug) {
          const sample = allRows.slice(0, 10).map(s => ({
            id: s.id,
            campus: s.campus,
            gradeLevel: s.gradeLevel,
            sections: s.sections,
            section: s.section
          }));
          setDebugInfo({
            filters: { campus, grade, section },
            counts: {
              total: allRows.length,
              campus: afterCampus.length,
              grade: afterGrade.length,
              section: afterSection.length
            },
            sample
          });
        } else {
          setDebugInfo(null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [campus, grade, section, debug]);

  const title = useMemo(() => `${grade} — ${section}`, [grade, section]);

  function exportCSV() {
    const headers = ["ID", "Full Name", "Grade", "Section", "Campus"];
    const rows = students.map(s => [s.id, formatName(s), s.gradeLevel || "", splitSections(s.section).join(" / "), campus]);
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${String(v || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `class_list_${campus}_${grade}_${section}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h3 className="mb-0">Class List</h3>
          <div className="text-muted">{title}</div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Back</button>
          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={students.length === 0}>Export CSV</button>
          <button className="btn btn-primary" onClick={() => window.print()} disabled={students.length === 0}>Print</button>
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 180 }}>Student ID</th>
                <th>Name</th>
                <th style={{ width: 160 }}>Grade</th>
                <th style={{ width: 220 }}>Section</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="text-center text-muted py-4">Loading…</td></tr>
              ) : students.length === 0 ? (
                <tr><td colSpan="4" className="text-center text-muted py-4">No students found</td></tr>
              ) : students.map(s => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>{formatName(s)}</td>
                  <td>{s.gradeLevel || ""}</td>
                  <td>{splitSections(s.section).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {debug && debugInfo && (
        <div className="card mt-3">
          <div className="card-body">
            <div style={{ fontWeight: 900, marginBottom: 8 }}>Debug</div>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(debugInfo, null, 2)}</pre>
          </div>
        </div>
      )}
      <style>{`@media print { .btn { display: none !important; } @page { size: letter portrait; margin: 12mm; } }`}</style>
    </div>
  );
}
